import axios from 'axios';
import { authEvents } from './authEvents';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://infrasolapi.localhost.com';

export const LAST_ACTIVITY_KEY = 'lastActivityAt';

export const SESSION_IDLE_MS = Number(
  process.env.REACT_APP_SESSION_IDLE_MS ?? 3600000
);
export const SESSION_WARN_MS = Number(
  process.env.REACT_APP_SESSION_WARN_MS ?? 300000
);
export const SESSION_REFRESH_LEAD_MS = Number(
  process.env.REACT_APP_SESSION_REFRESH_LEAD_MS ?? 300000
);

const CHECK_INTERVAL_MS = 60000;
const ACTIVITY_DEBOUNCE_MS = 30000;

let refreshInFlight: Promise<boolean> | null = null;
let activityDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export function parseTokenExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  const exp = parseTokenExp(token);
  if (!exp) return true;
  return Date.now() >= exp;
}

export function getLastActivityAt(): number {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function recordActivity(): void {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

export function clearSessionActivity(): void {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
}

export function getIdleMs(): number {
  const last = getLastActivityAt();
  if (!last) return 0;
  return Date.now() - last;
}

export function isIdle(): boolean {
  const last = getLastActivityAt();
  if (!last) return false;
  return getIdleMs() >= SESSION_IDLE_MS;
}

export function shouldRefreshToken(): boolean {
  const token = localStorage.getItem('token');
  if (!token || isIdle()) return false;

  const exp = parseTokenExp(token);
  if (!exp) return false;

  return exp - Date.now() <= SESSION_REFRESH_LEAD_MS;
}

export function shouldShowIdleWarning(): boolean {
  if (!localStorage.getItem('token')) return false;

  const idleMs = getIdleMs();
  const warnThreshold = SESSION_IDLE_MS - SESSION_WARN_MS;

  return idleMs >= warnThreshold && idleMs < SESSION_IDLE_MS;
}

export function getSessionCheckIntervalMs(): number {
  return CHECK_INTERVAL_MS;
}

export function getActivityDebounceMs(): number {
  return ACTIVITY_DEBOUNCE_MS;
}

async function performRefresh(): Promise<boolean> {
  const token = localStorage.getItem('token');
  if (!token) return false;

  try {
    const response = await refreshClient.post<{ token: string }>('/api/auth/refresh', {
      token,
    });
    const newToken = response.data?.token;
    if (!newToken) return false;

    localStorage.setItem('token', newToken);
    return true;
  } catch {
    return false;
  }
}

export async function refreshTokenIfNeeded(): Promise<boolean> {
  if (!shouldRefreshToken()) return false;

  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

export async function forceStayLoggedIn(): Promise<boolean> {
  recordActivity();
  const token = localStorage.getItem('token');
  if (!token) return false;

  if (refreshInFlight) {
    await refreshInFlight;
  }

  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

export function handleIdleLogout(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  clearSessionActivity();
  authEvents.emitUnauthorized();
}

export function recordActivityDebounced(): void {
  if (activityDebounceTimer) {
    clearTimeout(activityDebounceTimer);
  }
  activityDebounceTimer = setTimeout(() => {
    recordActivity();
    activityDebounceTimer = null;
  }, ACTIVITY_DEBOUNCE_MS);
}

export function clearActivityDebounce(): void {
  if (activityDebounceTimer) {
    clearTimeout(activityDebounceTimer);
    activityDebounceTimer = null;
  }
}

export async function runSessionMaintenance(): Promise<void> {
  const token = localStorage.getItem('token');
  if (!token) return;

  if (isTokenExpired(token)) {
    handleIdleLogout();
    return;
  }

  if (isIdle()) {
    handleIdleLogout();
    return;
  }

  await refreshTokenIfNeeded();
}

export function validateStoredSession(): boolean {
  const token = localStorage.getItem('token');
  if (!token) {
    clearSessionActivity();
    return false;
  }

  if (isTokenExpired(token)) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearSessionActivity();
    return false;
  }

  if (!getLastActivityAt()) {
    recordActivity();
  }

  return true;
}
